import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import React, { Suspense, lazy, useEffect } from "react";
import Navigation from "./components/Navigation";
import ImageProtection from "./components/ImageProtection";
import CustomCursor from "./components/CustomCursor";
import Home from "./pages/Home";
import Work from "./pages/Work";
import Studio from "./pages/Studio";
import Practice from "./pages/Practice";
import Contact from "./pages/Contact";
import { AuthProvider } from "./contexts/AuthContext";
import "./App.css";

// Lazy load heavy/infrequent pages
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const MarkdownConverter = lazy(() => import("./pages/MarkdownConverter"));
const Palette = lazy(() => import("./pages/Palette"));
const Vault = lazy(() => import("./pages/Vault"));
const Projects = lazy(() => import("./pages/Projects"));
const ProtectedRoute = lazy(() => import("./components/admin/ProtectedRoute"));
const SandhanMenu = lazy(() => import("./components/SandhanMenu"));

const LazyFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#002F6C]"></div>
  </div>
);

const AppContent = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <>
      <ImageProtection />
      <Suspense fallback={<LazyFallback />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<><Navigation /><Home /></>} />
          <Route path="/work" element={<><Navigation /><Work /></>} />
          <Route path="/work/:projectId" element={<><Navigation /><ProjectDetail /></>} />
          <Route path="/studio" element={<><Navigation /><Studio /></>} />
          <Route path="/practice" element={<><Navigation /><Practice /></>} />
          <Route path="/contact" element={<><Navigation /><Contact /></>} />
          <Route path="/projects" element={<><Navigation /><Projects /></>} />

          {/* Private Utilities */}
          <Route path="/markdown" element={<MarkdownConverter />} />
          <Route path="/palette" element={<Palette />} />
          <Route path="/vault" element={<Vault />} />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin" element={<AdminLogin />} />
        </Routes>
      </Suspense>

      {!isAdminRoute && (
        <Suspense fallback={null}>
          <SandhanMenu />
        </Suspense>
      )}
    </>
  );
};

function App() {
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/landing-page`)
      .then(r => r.json())
      .then(d => {
        // Title is now controlled per-route via react-helmet-async (see Seo component).
        // Keep only the dynamic favicon logic from site_settings.
        const faviconUrl = d?.site_settings?.favicon_url;
        if (faviconUrl) {
          const link = document.querySelector("link[rel~='icon']");
          if (link) link.href = faviconUrl;
        }
      })
      .catch(() => {}); // silent
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App">
          <CustomCursor />
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
